import type {
  MarketplaceProduct,
  MarketplaceProtocol,
  MarketplaceStack,
  MarketplaceStackTerm,
  StackRecovery,
  StackTerm,
} from '@/types/api';
import { cartItemFromProduct, stackGroups, type CartItem, type PlanPrices } from './stores/cart';

/** Terms a stack is sold at, in display order. */
export const STACK_TERMS: StackTerm[] = ['monthly', 'quarterly', 'sixMonth'];

export const STACK_TERM_LABELS: Record<StackTerm, string> = {
  monthly: 'Monthly',
  quarterly: '3 months',
  sixMonth: '6 months',
};

export const STACK_CADENCE: Record<StackTerm, string> = {
  monthly: '/mo',
  quarterly: '/3 mo',
  sixMonth: '/6 mo',
};

export function stackDollars(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: cents % 100 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

const positive = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0;

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);

export interface StackOffer {
  stack: MarketplaceStack;
  /** Sellable terms, in display order. */
  terms: StackTerm[];
  term(frequency: StackTerm): MarketplaceStackTerm;
  /** The stack's products, in the stack's line order. */
  products: MarketplaceProduct[];
}

/** The protocol's stack as it can be sold, or null when it isn't a priced
 *  stack. A stack whose lines don't match the card's products or the Shop's
 *  listings, or whose shares don't add up to its price, is never sold at a
 *  price we can't show exactly. */
export function stackOffer(
  protocol: MarketplaceProtocol,
  products: MarketplaceProduct[],
): StackOffer | null {
  const stack = protocol.stack;
  if (!stack?.id || !Array.isArray(stack.terms)) return null;
  const byCanonical = new Map(
    products
      .filter((product) => product.canonical_product_id)
      .map((product) => [product.canonical_product_id as string, product]),
  );
  const members = [
    ...new Set(protocol.products.map((item) => item.canonical_product_id)),
  ].sort();
  const sameProducts = (term: MarketplaceStackTerm) =>
    JSON.stringify(term.lines.map((line) => line.canonical_product_id).sort()) ===
    JSON.stringify(members);
  const terms = STACK_TERMS.flatMap((frequency) => {
    const term = stack.terms.find((t) => t?.frequency === frequency);
    const valid =
      term &&
      Array.isArray(term.lines) &&
      term.lines.length >= 2 &&
      sameProducts(term) &&
      term.lines.every(
        (line) =>
          positive(line.price_cents) &&
          positive(line.member_price_cents) &&
          byCanonical.get(line.canonical_product_id)?.purchase_mode !==
            'one_time_course' &&
          byCanonical.has(line.canonical_product_id),
      ) &&
      positive(term.price_cents) &&
      sum(term.lines.map((line) => line.price_cents)) === term.price_cents &&
      positive(term.member_price_cents) &&
      sum(term.lines.map((line) => line.member_price_cents)) ===
        term.member_price_cents;
    return valid ? [term] : [];
  });
  if (!terms.length) return null;
  const byFrequency = new Map(terms.map((term) => [term.frequency, term]));
  return {
    stack,
    terms: terms.map((term) => term.frequency),
    term: (frequency) => byFrequency.get(frequency)!,
    products: terms[0].lines.map((line) => byCanonical.get(line.canonical_product_id)!),
  };
}

/** The stack's cart lines, locked to one term and carrying the stack id. Each
 *  line keeps its single prices so it can revert if it is left on its own. */
export function stackCartItems(
  offer: StackOffer,
  protocol: MarketplaceProtocol,
  frequency: StackTerm,
): Omit<CartItem, 'quantity'>[] {
  return offer.products.map((product) => {
    const share: PlanPrices = {};
    for (const term of offer.terms)
      share[term] = offer
        .term(term)
        .lines.find((line) => line.canonical_product_id === product.canonical_product_id)!
        .price_cents;
    const single = cartItemFromProduct(product);
    return {
      id: product.id,
      slug: product.slug,
      title: product.title,
      subtitle: product.treatment_support ?? product.subtitle ?? undefined,
      image_url: product.hero_image_url ?? null,
      origin: 'product',
      frequency,
      price_cents: share[frequency] as number,
      plan_prices: share,
      // A stack line renews at its share.
      renewal_prices: share,
      single_plan_prices: single.plan_prices,
      single_renewal_prices: single.renewal_prices,
      stack_id: offer.stack.id,
      stack_title: offer.stack.title || protocol.title,
      stack_slug: protocol.slug,
      stack_product: product.canonical_product_id ?? undefined,
    };
  });
}

/** Sum of the single prices for a stack group's current term — what the
 *  same products cost bought separately. */
export function stackGroupCompareCents(lines: CartItem[]): number {
  return lines.reduce((total, line) => {
    const term = line.frequency ?? 'monthly';
    return total + (line.single_plan_prices?.[term] ?? line.price_cents);
  }, 0);
}

/** The cart line(s) a pay link buys: the listed product(s) alone, at their
 *  stack share on the recovery's term. Null when a listed product isn't in
 *  the Shop or its price isn't usable. */
export function stackRecoveryCartItems(
  recovery: StackRecovery,
  products: MarketplaceProduct[],
): Omit<CartItem, 'quantity'>[] | null {
  if (!STACK_TERMS.includes(recovery.frequency) || !recovery.items?.length) return null;
  const lines = recovery.items.map((item) => {
    const product = products.find(
      (candidate) => candidate.canonical_product_id === item.canonical_product_id,
    );
    if (!product || !positive(item.price_cents)) return null;
    const single = cartItemFromProduct(product);
    const share: PlanPrices = { [recovery.frequency]: item.price_cents };
    return {
      id: product.id,
      slug: product.slug,
      title: product.title,
      subtitle: product.treatment_support ?? product.subtitle ?? undefined,
      image_url: product.hero_image_url ?? null,
      origin: 'product' as const,
      frequency: recovery.frequency,
      price_cents: item.price_cents,
      plan_prices: share,
      renewal_prices: share,
      single_plan_prices: single.plan_prices,
      single_renewal_prices: single.renewal_prices,
      stack_id: recovery.stack_id,
      stack_title: recovery.stack_title,
      stack_recovery: recovery.slug,
      stack_product: item.canonical_product_id,
    };
  });
  return lines.every(Boolean) ? (lines as Omit<CartItem, 'quantity'>[]) : null;
}

/** What a stack in the cart has to become to match a fresh Shop listing:
 *  `lines` to replace it with (repriced; `message` empty when today's price
 *  is unchanged), or null to take it out (the Shop no longer sells it to this
 *  patient: switched off, a product held or owned, an unusable price).
 *  Pay-link lines keep the price they were sold at and are never changed. */
export interface StackCartChange {
  stackId: string;
  lines: Omit<CartItem, 'quantity'>[] | null;
  message: string;
}

const STACK_LINE_FIELDS = [
  'frequency',
  'price_cents',
  'plan_prices',
  'renewal_prices',
  'single_plan_prices',
  'single_renewal_prices',
  'stack_product',
] as const;

export function stackCartChanges(
  items: CartItem[],
  listing: { protocols?: MarketplaceProtocol[]; products?: MarketplaceProduct[] },
): StackCartChange[] {
  const changes: StackCartChange[] = [];
  for (const [stackId, lines] of stackGroups(items)) {
    if (lines.some((line) => line.stack_recovery)) continue;
    const title = lines[0].stack_title ?? lines.map((line) => line.title).join(' + ');
    const protocol = (listing.protocols ?? []).find((p) => p.stack?.id === stackId);
    const offer = protocol ? stackOffer(protocol, listing.products ?? []) : null;
    if (!protocol || !offer) {
      changes.push({
        stackId,
        lines: null,
        message: `The ${title} isn't available to you right now, so it was removed from your cart. Nothing was charged.`,
      });
      continue;
    }
    const current = (lines[0].frequency ?? 'monthly') as StackTerm;
    const term = offer.terms.includes(current) ? current : offer.terms[0];
    const expected = stackCartItems(offer, protocol, term);
    const same =
      expected.length === lines.length &&
      expected.every((want) => {
        const line = lines.find((candidate) => candidate.id === want.id);
        return (
          line &&
          STACK_LINE_FIELDS.every(
            (field) => JSON.stringify(line[field]) === JSON.stringify(want[field]),
          )
        );
      });
    if (same) continue;
    const before = sum(lines.map((line) => line.price_cents));
    const after = sum(expected.map((line) => line.price_cents));
    changes.push({
      stackId,
      lines: expected,
      message:
        term === current && before === after
          ? ''
          : `The ${title} is now ${stackDollars(after)}${STACK_CADENCE[term]}. Review your cart before confirming.`,
    });
  }
  return changes;
}
